import {
  clearPendingSession,
  readPendingSession,
  storePendingSession,
} from "../pending-session";
import { AdyenCheckout, Dropin } from "@adyen/adyen-web/auto";
import "@adyen/adyen-web/styles/adyen.css";
import { parsePaymentReturn, paymentSessionIdFromQuery } from "../return-status";
import { PaymentProviderName } from "@/lib/config";
import type {
  PaymentConfirmation,
  PaymentProvider,
  PaymentSession,
  ReturnContext,
  StartCheckoutContext,
} from "../types";
import type { PaymentEnvironment } from "@/lib/config";

interface AdyenDropinInstance {
  mount(element: HTMLElement): void;
  unmount?(): void;
}

/**
 * Adyen integration. `parseReturn` is functional — Adyen redirects to the
 * return URL with resultCode/redirectResult parameters.
 */
export class AdyenPaymentProvider implements PaymentProvider {
  readonly name = PaymentProviderName.Adyen;
  private dropin: AdyenDropinInstance | null = null;

  constructor(private readonly environment: PaymentEnvironment = "test") {}

  async startCheckout(session: PaymentSession, ctx: StartCheckoutContext): Promise<void> {
    if (!ctx.mountEl) {
      throw new Error("Adyen Drop-in mount element is missing.");
    }
    if (!session.sessionData) {
      throw new Error("Adyen checkout session is missing sessionData.");
    }
    if (!session.clientKey) {
      throw new Error("Adyen checkout session is missing clientKey.");
    }

    storePendingSession({
      paymentSessionId: session.paymentSessionId ?? session.sessionId,
      providerSessionId: session.sessionId,
      provider: this.name,
      orderReference: session.orderReference,
    });

    const checkout = await AdyenCheckout({
      environment: this.environment,
      clientKey: session.clientKey,
      session: {
        id: session.sessionId,
        sessionData: session.sessionData,
      },
      onPaymentCompleted: (result: { resultCode?: string; merchantReference?: string }) => {
        clearPendingSession();
        ctx.onComplete?.({
          ...(result.resultCode ? { resultCode: result.resultCode } : {}),
          ...(result.merchantReference ? { merchantReference: result.merchantReference } : {}),
        });
      },
      onPaymentFailed: (result?: { resultCode?: string; merchantReference?: string }) => {
        clearPendingSession();
        ctx.onFailed?.({
          ...(result?.resultCode ? { resultCode: result.resultCode } : {}),
          ...(result?.merchantReference ? { merchantReference: result.merchantReference } : {}),
        });
      },
    });

    this.dropin?.unmount?.();
    ctx.mountEl.replaceChildren();
    this.dropin = new Dropin(checkout, {
      openFirstPaymentMethod: true,
    });
    this.dropin.mount(ctx.mountEl);
  }

  parseReturn(ctx: ReturnContext): PaymentConfirmation {
    const pending = readPendingSession();
    const parsed = parsePaymentReturn(ctx.query);
    clearPendingSession();
    return {
      sessionId: pending?.paymentSessionId ?? paymentSessionIdFromQuery(ctx.query) ?? "",
      paymentProviderSessionId:
        pending?.providerSessionId ?? paymentSessionIdFromQuery(ctx.query),
      provider: this.name,
      outcome: parsed.outcome,
      orderReference: parsed.orderReference ?? pending?.orderReference,
      resultCode: parsed.resultCode,
      providerData: { ...ctx.query },
    };
  }
}
